<template>
  <div data-testid="file__root" :class="['d-flex', { 'justify-center': centered || dropzone }]">
    <div v-if="fileList.length === 0 || multiple || true" data-testid="file__container" :class="{ 'flex-grow-1': dropzone }">
      <v-file-input
        :accept="accept"
        class="d-none filebtn"
        ref="fileinput"
        :label="label"
        v-model="content"
        :multiple="multiple"
        @change="updateValue()"
        :hide-details="hidedetails"
        :disabled="disabled"
        data-testid="file__input"
      ></v-file-input>
      
      <!-- Dropzone Mode -->
      <div
        v-if="dropzone"
        class="file-dropzone pa-6 text-center rounded"
        :class="{ 'dropzone-active': isDragging }"
        @dragover.prevent="isDragging = true"
        @dragleave.prevent="isDragging = false"
        @drop.prevent="handleDropzoneDrop"
        data-testid="file__dropzone-area"
      >
        <v-icon size="48" :color="isDragging ? 'primary' : 'grey'" data-testid="file__dropzone-icon">mdi-cloud-upload-outline</v-icon>
        <div class="mt-2 text-body-1" :class="isDragging ? 'primary--text' : 'grey--text'" data-testid="file__dropzone-text">
          {{ dropzoneText || 'Drag and drop your file here' }}
        </div>
        <div class="mt-1 text-caption grey--text" data-testid="file__dropzone-divider">or</div>
        <div class="mt-2 d-flex justify-center" data-testid="file__dropzone-btn-wrapper">
          <v-tooltip bottom content-class="tooltip-bottom">
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                v-bind="attrs" v-on="on"
                :loading="loading"
                v-if="(!content || reupload) && !disabled"
                @click="clickupload()"
                class="shadow-off secondaryBtn"
                :icon="icononly"
                :text="!icononly"
                data-testid="file__upload-btn"
              >
                <template v-slot:loader>
                  <v-progress-circular
                    indeterminate
                    size="17"
                    width="2"
                    class="mr-1"
                    data-testid="file__upload-loader"
                  ></v-progress-circular>
                  <span data-testid="file__upload-percent">{{ Number(loadingpercent || 0).toFixed(0) }}%</span>
                </template>
                <v-icon :left="!icononly" data-testid="file__upload-icon">mdi-upload</v-icon><span v-if="!icononly" data-testid="file__upload-label">{{ content ? 'Re-Upload' : (label || 'Upload') }}</span>
              </v-btn>
            </template>
            <span data-testid="file__upload-tooltip">Upload File</span>
          </v-tooltip>
        </div>
        <template v-if="displayname && fileList.length > 0">
          <div
            v-for="(v, k) in fileList"
            :key="k"
            class="mt-2"
            data-testid="file__selected-file"
          >
            <span class="blue--text text--darken-4 pr-3 py-1" data-testid="file__selected-file-name">{{ v.name }}</span>
            <span
              class="grey--text text--darken-1 caption"
              v-if="v.size > 1024 * 1024"
              data-testid="file__selected-file-size-mb"
            >{{ (v.size / (1024 * 1024)).toFixed(2) }}MB</span>
            <span class="grey--text text--darken-1 caption" v-else data-testid="file__selected-file-size-kb">{{ (v.size / 1024).toFixed(0) }}KB</span>
            <v-btn
              v-if="remove"
              class="ml-2"
              icon
              small
              @click="removeFile(k)"
              data-testid="file__remove-btn"
            ><v-icon small data-testid="file__remove-icon">mdi-close</v-icon></v-btn>
          </div>
        </template>
        <div v-if="dropzoneHint" class="mt-2 text-caption grey--text" data-testid="file__dropzone-hint">{{ dropzoneHint }}</div>
      </div>
      
      <!-- Standard Mode -->
      <div
        v-else
        :class="{
          'uploaddropbox drop-container px-2 py-1 uploadbox bg-white': drag,
          uploadboxerror: !!error && !content,
        }"
        @drop.prevent="(e) => dropFiles(e)"
        @dragover.prevent
        :data-testid="drag ? 'file__dropzone--dragging' : 'file__dropzone--idle'"
      >
        <div data-testid="file__dropzone-content">
          <div class="drop-container-default" data-testid="file__dropzone-default">
            <div :class="['d-flex', 'align-center', { 'justify-center': centered }]" data-testid="file__header-row">
              <span v-if="label" class="mr-4" data-testid="file__label">{{ label }}</span>
              <v-tooltip bottom content-class="tooltip-bottom">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    v-bind="attrs" v-on="on"
                    :loading="loading"
                    v-if="(!content || reupload) && !disabled"
                    @click="clickupload()"
                    class="shadow-off secondaryBtn"
                    :icon="icononly"
                    :text="!icononly"
                    data-testid="file__upload-btn"
                  >
                    <template v-slot:loader>
                      <v-progress-circular
                        indeterminate
                        size="17"
                        width="2"
                        class="mr-1"
                        data-testid="file__upload-loader"
                      ></v-progress-circular>
                      <span data-testid="file__upload-percent">{{ Number(loadingpercent || 0).toFixed(0) }}%</span>
                    </template>
                    <v-icon :left="!icononly" data-testid="file__upload-icon">mdi-upload</v-icon><span v-if="!icononly" data-testid="file__upload-label">Upload</span>
                  </v-btn>
                </template>
                <span data-testid="file__upload-tooltip">Upload File</span>
              </v-tooltip>
              <v-spacer v-if="displayname" data-testid="file__spacer"></v-spacer>
              <template v-if="displayname">
                <div
                  v-for="(v, k) in fileList"
                  :key="k"
                  :class="{ 'display-ib': !multiple, 'ml-2': true }"
                  data-testid="file__selected-file"
                >
                  <div data-testid="file__selected-file-row">
                    <span class="blue--text text--darken-4 pr-3 py-1" data-testid="file__selected-file-name">{{
                      v.name
                    }}</span>
                    <span
                      class="grey--text text--darken-1 caption"
                      v-if="v.size > 1024 * 1024"
                      data-testid="file__selected-file-size-mb"
                      >{{ (v.size / (1024 * 1024)).toFixed(2) }}MB</span
                    >
                    <span class="grey--text text--darken-1 caption" v-else
                      data-testid="file__selected-file-size-kb"
                      >{{ (v.size / 1024).toFixed(0) }}KB</span
                    >
                    <v-btn
                      v-if="remove"
                      class="ml-2"
                     
                      icon
                      @click="removeFile(k)"
                      data-testid="file__remove-btn"
                      ><v-icon data-testid="file__remove-icon">mdi-close</v-icon>
                    </v-btn>
                  </div>
                </div>
              </template>
              <v-tooltip bottom content-class="tooltip-bottom" v-if="id" data-testid="file__download-tooltip">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    v-bind="attrs"
                    v-on="on"
                    class="shadow-off"
                    icon
                    @click="$nova.downloadFile(id)"
                    data-testid="file__download-btn"
                  >
                    <v-icon data-testid="file__download-icon">mdi-download</v-icon></v-btn
                  >
                </template>
                <span data-testid="file__download-tooltip-label">Download</span>
              </v-tooltip>
            </div>
          </div>
        </div>
      </div>
      <div class="error--text" style="min-height: 20px" v-if="!hidedetails" data-testid="file__error-container">
        <div v-if="!content" data-testid="file__error-message">
          {{ error instanceof Array ? error[0] || "" : error }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  activated() {
    this.setValue();
    // this.updateValue();
  },
  created() {
    this.setValue();
    // this.updateValue();
  },
  data: () => ({
    content: null,
    isDragging: false,
  }),
  props: {
    error: {
      type: [String, Array],
    },
    label: {
      type: String,
      default: "File",
    },
    id: {
      type: String,
      default: "",
    },
    value: {
      // type: String
      default: null,
    },
    accept: {
      type: String,
      default: "",
    },
    loadingpercent: {
      type: [String, Number],
      default: 0,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    fullwidth: {
      type: Boolean,
      default: false,
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    reupload: {
      type: Boolean,
      default: true,
    },
    remove: {
      type: Boolean,
      default: false,
    },
    drag: {
      type: Boolean,
      default: true,
    },
    hidedetails: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    displayname: {
      type: Boolean,
      default: true,
    },
    icononly: {
      type: Boolean,
      default: false,
    },
    color: {
      type: String,
      default: "primary",
    },
    centered: {
      type: Boolean,
      default: false,
    },
    dropzone: {
      type: Boolean,
      default: false,
    },
    dropzoneText: {
      type: String,
      default: '',
    },
    dropzoneHint: {
      type: String,
      default: '',
    },
    // required: {
    //   type: Boolean,
    //   default: false
    // }
  },
  methods: {
    setValue: function () {
      if (this.multiple) {
        if (this.value instanceof Array) this.content = this.value;
        else this.content = [];
      } else {
        if (this.value instanceof File) this.content = this.value;
        else this.content = null;
      }
      // When value is cleared, reset the native file input so the same file
      // can be re-selected (browsers won't fire 'change' if the input still
      // holds the previously chosen file)
      const isEmpty = !this.value || (Array.isArray(this.value) && this.value.length === 0);
      if (isEmpty) {
        this.$nextTick(() => {
          try {
            const nativeInput = this.$refs.fileinput?.$el?.querySelector('input');
            if (nativeInput) nativeInput.value = '';
          } catch (e) { /* refs may not be mounted yet on initial call */ }
        });
      }
    },
    updateValue: function () {
      this.$emit("input", this.content);
      this.$emit("change", this.content);
    },
    setFocus: function () {
      this.$nextTick(() => {
        this.$refs.element.$el.querySelector("input").focus();
      });
    },
    dropFiles: function (d) {
      let droppedFiles = d.dataTransfer.items;

      if (droppedFiles) {
        let fl = [...droppedFiles];
        let ec = fl.length;
        if (!this.multiple) ec = 1;
        for (let i = 0; i < ec; i++) {
          let el = fl[i];
          if (el.webkitGetAsEntry().isFile) {
            el.webkitGetAsEntry().file((fil) => {
              if (!this.multiple) this.content = fil;
              else this.content.push(fil);
              this.updateValue();
            });
          }
        }
      }
    },
    clickupload: function () {
      this.$refs.fileinput.$el.querySelector("input").click();
    },
    removeFile: function (k) {
      if (this.multiple) this.content.splice(k, 1);
      else this.content = null;
      this.updateValue();
    },
    handleDropzoneDrop: function (e) {
      this.isDragging = false;
      const droppedFiles = e.dataTransfer.files;
      if (droppedFiles && droppedFiles.length > 0) {
        const file = droppedFiles[0];
        // Validate file extension if accept prop is provided
        if (this.accept) {
          const acceptedExtensions = this.accept.split(',').map(ext => ext.trim().toLowerCase());
          const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
          if (!acceptedExtensions.includes(fileExtension)) {
            this.$emit('invalid-file', { file, message: `Invalid file type. Accepted formats: ${this.accept}` });
            return;
          }
        }
        if (!this.multiple) {
          this.content = file;
        } else {
          this.content.push(file);
        }
        this.updateValue();
      }
    },
  },
  computed: {
    fileList: function () {
      let d = [];
      if (this.content instanceof Array) d = this.content;
      else if (this.content instanceof File) d = [this.content];

      return d;
    },
  },
  watch: {
    value: function () {
      this.setValue();
    },
    // loadingpercent: function(e){
    //   console.log("fileee", e);
    // }
  },
};
</script>

<style scoped>
.uploadbox {
  border: 1px solid #999;
  border-radius: 5px;
}
/* .uploadbox .v-btn{
  color: #888;
} */
.uploadboxerror {
  border: 2px solid var(--v-error-base) !important;
}
.uploadboxerror *,
.uploadboxerror .v-btn * {
  color: var(--v-error-base) !important;
}

/* Dropzone Mode Styles */
.file-dropzone {
  border: 2px dashed #ccc;
  background-color: #fafafa;
  transition: all 0.3s ease;
  cursor: pointer;
}

.file-dropzone .v-icon {
  font-size: 48px !important;
}

.file-dropzone:hover {
  border-color: #1976d2;
  background-color: #f5f9ff;
}

.file-dropzone.dropzone-active {
  border-color: #1976d2;
  background-color: #e3f2fd;
  border-style: solid;
}
</style>